import React, { useState } from "react";
import "./BMICalculator.css";

const BMICalculator = () => {
  const [age, setAge] = useState("");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");
  const [weight, setWeight] = useState("");
  const [result, setResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  const dietPlans = {
    "Underweight": {
      breakfast: ["Idli with peanut chutney", "Aloo paratha with curd", "Pesara dosa with ghee", "Paneer stuffed paratha with butter"],
      lunch: ["Chicken curry with rice", "Rajma chawal", "Fish curry with steamed rice", "Dal makhani with naan"],
      snacks: ["Banana milkshake", "Boiled groundnuts", "Samosa (moderate)", "Egg bajji", "Roasted cashews"],
      dinner: ["Chapati with egg curry", "Paneer butter masala with roti", "Pesarattu with upma", "Vegetable pulao with curd"]
      
    },
    "Normal weight": {
      breakfast: ["Upma with vegetables", "Poha with peanuts", "Idli with sambar", "Stuffed vegetable paratha with curd"],
      lunch: ["Dal with rice and vegetable curry", "Chole with chapati", "Fish fry with rasam", "Vegetable biryani with raita"],
      snacks: ["Sprouts chaat", "Buttermilk", "Fruit bowl (papaya, guava, apple)", "Roasted chana", "Masala chai with murmura"],
      dinner: ["Chapati with dal and sabji", "Vegetable pulao", "Grilled fish with rice", "Palak paneer with roti"]
      
    },
    "Overweight": {
      breakfast: ["Ragi dosa with chutney", "Vegetable poha", "Idli with sambar (no ghee)", "Moong dal chilla with green chutney"],
      lunch: ["Brown rice with sambar and boiled vegetables", "Grilled chicken with salad", "Dal with jowar roti", "Missi roti with lauki sabji"],
      snacks: ["Cucumber slices", "Green tea", "Boiled chana", "Roasted makhana", "Watermelon"],
      dinner: ["Oats khichdi", "Chapati with ridge gourd curry", "Grilled fish with spinach curry", "Methi thepla with curd"]
      
    },
    "Obese": {
      breakfast: ["Vegetable oats", "Ragi porridge (unsweetened)", "Idli (2 pcs) with ginger chutney", "Moong dal sprouts salad"],
      lunch: ["Thin dal with brown rice (small portion)", "Steamed vegetables with rasam", "Jowar roti with bottle gourd curry", "Tinda sabji with phulka"],
      snacks: ["Buttermilk with curry leaves", "Cucumber and carrot sticks", "Lemon water", "Guava", "Roasted lotus seeds (makhana)"],
      dinner: ["Vegetable soup", "Cabbage curry with chapati", "Tomato rasam with boiled beans", "Lauki sabji with roti"]
      
    }
  };

  const calculateBMI = (e) => {
    e.preventDefault();
    if (!age || !feet || !weight || parseFloat(feet) <= 0 || parseFloat(weight) <= 0) return;

    // Convert feet + inches → meters
    const totalInches = (parseFloat(feet) * 12) + (parseFloat(inches) || 0);
    const heightInMeters = totalInches * 0.0254;

    // Convert weight to number
    const weightKg = parseFloat(weight);

    // Apply BMI formula
    const bmiValue = weightKg / (heightInMeters * heightInMeters);

    // Determine category and suggestions
    let category = "";
    let suggestion = "";

    if (bmiValue < 18.5) {
      category = "Underweight";
      suggestion = "You are underweight. Focus on nutrient-dense, calorie-rich foods and consider consulting a nutritionist.";
    } else if (bmiValue < 24.9) {
      category = "Normal weight";
      suggestion = "Great! You have a healthy weight. Continue maintaining your balanced lifestyle.";
    } else if (bmiValue < 29.9) {
      category = "Overweight";
      suggestion = "You are overweight. Focus on portion control, regular exercise, and a balanced diet.";
    } else {
      category = "Obese";
      suggestion = "You are in the obese category. Seek professional guidance for weight management and focus on sustainable lifestyle changes.";
    }

    const newResult = {
      bmi: bmiValue,
      category,
      suggestion
    };

    setResult(newResult);
    setShowResult(true);
  };

  const reset = () => {
    setAge("");
    setFeet("");
    setInches("");
    setWeight("");
    setResult(null);
    setShowResult(false);
  };

  const getBMICategoryColor = (category) => {
    switch (category) {
      case "Underweight":
        return "category-underweight";
      case "Normal weight":
        return "category-normal";
      case "Overweight":
        return "category-overweight";
      case "Obese":
        return "category-obese";
      default:
        return "";
    }
  };

  const getBMIPosition = (bmi) => {
    if (bmi <= 18.5) return (bmi / 18.5) * 25;
    if (bmi <= 25) return 25 + ((bmi - 18.5) / (25 - 18.5)) * 25;
    if (bmi <= 30) return 50 + ((bmi - 25) / (30 - 25)) * 25;
    return Math.min(75 + ((bmi - 30) / 10) * 25, 100);
  };

  const getBMICategory = (bmi) => {
    if (bmi < 18.5) return "Underweight";
    if (bmi < 25) return "Normal weight";
    if (bmi < 30) return "Overweight";
    return "Obese";
  };

  const getMealIcon = (mealType) => {
    const icons = {
      'Breakfast': '🌅',
      'Lunch': '☀️', 
      'Dinner': '🌙',
      'Snacks': '☕'
    };
    return icons[mealType] || '🍽️';
  };

  const bmiRanges = [
    { label: "Underweight", min: 0, max: 18.5, color: "bmi-underweight" },
    { label: "Normal weight", min: 18.5, max: 24.9, color: "bmi-normal" },
    { label: "Overweight", min: 25, max: 29.9, color: "bmi-overweight" },
    { label: "Obese", min: 30, max: 40, color: "bmi-obese" }
  ];

  return (
    <div className="bmi-page">
      {/* Header */}
      <div className="bmi-header">
        <div className="header-content">
          <div className="header-icon">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"/>
            </svg>
          </div>
          <div className="header-text">
            <h1>BMI Calculator</h1>
            <p>Calculate your Body Mass Index and get personalized diet recommendations</p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content1">
        <div className="content-grid">
          {/* Form Section */}
          <div className="form-section">
            <div className="form-card">
              <div className="form-header">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                </svg>
                <h2>Enter Your Details</h2>
              </div>

              <form onSubmit={calculateBMI} className="bmi-form">
                <div className="input-group">
                  <label htmlFor="age">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                      <circle cx="12" cy="7" r="4"/>
                    </svg>
                    Age (years)
                  </label>
                  <input
                    id="age"
                    type="number"
                    placeholder="Enter your age"
                    value={age}
                    onChange={(e) => setAge(e.target.value)}
                    required
                  />
                </div>

                <div className="height-inputs">
                  <div className="input-group">
                    <label htmlFor="feet">Height (feet)</label>
                    <input
                      id="feet"
                      type="number"
                      placeholder="Feet"
                      value={feet}
                      onChange={(e) => setFeet(e.target.value)}
                      required
                    />
                  </div>
                  <div className="input-group">
                    <label htmlFor="inches">Height (inches)</label>
                    <input
                      id="inches"
                      type="number"
                      placeholder="Inches"
                      value={inches}
                      onChange={(e) => setInches(e.target.value)}
                    />
                  </div>
                </div>

                <div className="input-group">
                  <label htmlFor="weight">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
                    </svg>
                    Weight (kg)
                  </label>
                  <input
                    id="weight"
                    type="number"
                    step="0.1"
                    placeholder="Enter your weight"
                    value={weight}
                    onChange={(e) => setWeight(e.target.value)}
                    required
                  />
                </div>

                <div className="button-group">
                  <button type="submit" className="btn-calculate">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                    </svg>
                    Calculate BMI
                  </button>
                  {showResult && (
                    <button type="button" className="btn-reset" onClick={reset}>
                      Reset
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Results */}
            {showResult && result && (
              <div className="result-card">
                <div className="result-header">
                  <div className="result-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
                    </svg>
                  </div>
                </div>
                
                <div className="result-values">
                  <div className="bmi-value">{result.bmi.toFixed(1)}</div>
                  <div className={`bmi-category ${getBMICategoryColor(result.category)}`}>
                    {result.category}
                  </div>
                </div>

                <div className="result-suggestion">
                  <h4>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="22,12 18,12 15,21 9,3 6,12 2,12"/>
                    </svg>
                    Health Suggestion
                  </h4>
                  <p>{result.suggestion}</p>
                </div>
              </div>
            )}
          </div>

          {/* Chart Section */}
          <div className="chart-section">
            <div className="chart-card">
              <div className="chart-header">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="20" x2="18" y2="10"/>
                  <line x1="12" y1="20" x2="12" y2="4"/>
                  <line x1="6" y1="20" x2="6" y2="14"/>
                </svg>
                <h2>BMI Scale</h2>
              </div>

              {/* Visual BMI Scale */}
              <div className="bmi-scale-visual">
                <div className="bmi-bar">
                  <div className="bmi-segment bmi-underweight"></div>
                  <div className="bmi-segment bmi-normal"></div>
                  <div className="bmi-segment bmi-overweight"></div>
                  <div className="bmi-segment bmi-obese"></div>
                </div>

                <div className="bmi-labels">
                  <span>0</span>
                  <span>18.5</span>
                  <span>25</span>
                  <span>30</span>
                  <span>40+</span>
                </div>

                {/* User BMI Indicator */}
                {result && (
                  <div 
                    className="bmi-indicator"
                    style={{ left: `${Math.min(getBMIPosition(result.bmi), 95)}%` }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <circle cx="12" cy="12" r="6"/>
                      <circle cx="12" cy="12" r="2"/>
                    </svg>
                    <div className="bmi-indicator-value">
                      {result.bmi.toFixed(1)}
                    </div>
                  </div>
                )}
              </div>

              {/* Category Details */}
              <div className="bmi-categories">
                {bmiRanges.map((range, index) => (
                  <div
                    key={range.label}
                    className={`category-item ${result && getBMICategory(result.bmi) === range.label ? 'active' : ''}`}
                  >
                    <div className="category-info">
                      <div className={`category-color ${range.color}`}></div>
                      <div className="category-details">
                        <div className="category-name">{range.label}</div>
                        <div className="category-range">
                          {index === 0 ? "Below 18.5" : 
                           index === 3 ? "30.0 and above" :
                           `${range.min} - ${range.max}`}
                        </div>
                      </div>
                    </div>
                    
                    {result && getBMICategory(result.bmi) === range.label && (
                      <div className="your-range">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"/>
                          <circle cx="12" cy="12" r="6"/>
                          <circle cx="12" cy="12" r="2"/>
                        </svg>
                        <span>Your Range</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Health Tips */}
              <div className="health-tips">
                <h3>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"/>
                    <circle cx="12" cy="12" r="6"/>
                    <circle cx="12" cy="12" r="2"/>
                  </svg>
                  Health Tips
                </h3>
                <ul>
                  <li>Maintain a balanced diet with fruits, vegetables, and lean proteins</li>
                  <li>Exercise regularly - at least 150 minutes of moderate activity per week</li>
                  <li>Stay hydrated and get adequate sleep</li>
                  <li>Consult healthcare professionals for personalized advice</li>
                  <li>Track your progress with regular BMI calculations</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Full Screen Diet Plan */}
        {showResult && result && (
          <div className="diet-fullscreen">
            <div className="diet-fullscreen-header">
              <div className="diet-title-badge">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 2v7c0 6 4 10 9 12 5-2 9-6 9-12V2l-9 2-9-2Z"/>
                </svg>
                <h2>Personalized Diet Plan for {result.category}</h2>
              </div>
            </div>
            
            <div className="diet-grid-fullscreen">
              {Object.entries(dietPlans[result.category]).map(([mealType, foods], index) => (
                <div key={mealType} className={`meal-card-fullscreen meal-${index + 1}`}>
                  <div className="meal-header-fullscreen">
                    <div className="meal-icon-fullscreen">
                      {getMealIcon(mealType.charAt(0).toUpperCase() + mealType.slice(1))}
                    </div>
                    <h3>{mealType.charAt(0).toUpperCase() + mealType.slice(1)}</h3>
                  </div>
                  
                  <div className="meal-foods-fullscreen">
                    {foods.map((food, foodIndex) => (
                      <div key={foodIndex} className="food-item-fullscreen">
                        {food}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BMICalculator;